import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';

import { authenticationSession } from '@/lib/authentication-session';
import { AuthenticationResponse, ErrorCode } from '@activepieces/shared';

import { API_URL, isUnauthenticatedRoute, isRefreshTokenExcludedRoute } from './api-constants';

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null = null): void {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) return reject(error);
    if (token) return resolve(token);

    reject(new Error('Token refresh resolved without a token'));
  });

  failedQueue = [];
}

async function refreshToken(): Promise<string> {
  const response = await axios.post<AuthenticationResponse>(
    `${API_URL}/v1/authentication/refresh`,
    {},
    { withCredentials: true },
  );
  const token = response.data.token;
  if (!token) {
    throw new Error('Refresh response did not contain a token');
  }
  authenticationSession.saveResponse(response.data);
  return token;
}

export async function handleAuthError(
  error: AxiosError,
  originalConfig: AxiosRequestConfig,
): Promise<AxiosResponse> {
  const errorCode: ErrorCode | undefined = (
    error.response?.data as { code: ErrorCode }
  )?.code;

  const isRefreshableError = errorCode === ErrorCode.INVALID_BEARER_TOKEN;
  const url = (originalConfig.url ?? '').replace(API_URL, '');
  const isAuthRoute = isUnauthenticatedRoute(url);
  const isRefreshExcluded = isRefreshTokenExcludedRoute(url);

  if (
    !isRefreshableError ||
    isAuthRoute ||
    isRefreshExcluded
  ) {
    if (
      errorCode === ErrorCode.SESSION_EXPIRED ||
      errorCode === ErrorCode.INVALID_BEARER_TOKEN
    ) {
      authenticationSession.logOut();
    }
    throw error;
  }

  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    }).then((token) => {
      originalConfig.headers = {
        ...originalConfig.headers,
        Authorization: `Bearer ${token}`,
      };
      return axios(originalConfig);
    });
  }

  isRefreshing = true;

  return refreshToken()
    .then((newToken) => {
      processQueue(null, newToken);
      originalConfig.headers = {
        ...originalConfig.headers,
        Authorization: `Bearer ${newToken}`,
      };
      return axios(originalConfig);
    })
    .catch((refreshError) => {
      processQueue(refreshError, null);
      authenticationSession.logOut();
      throw refreshError;
    })
    .finally(() => {
      isRefreshing = false;
    });
}
